import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, Search, Filter, Download, Edit, Trash2, Eye, RefreshCw,
  Building2, FileText, Loader2, AlertCircle, Check, X, Calendar,
  DollarSign, IndianRupee, ChevronDown, ChevronUp, Send, Link, Copy, Share2
} from 'lucide-react';

import InvoiceEmailModal from './InvoiceEmailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand logos
const BRAND_LOGOS = {
  brandstage: "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png",
  continex: "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/48hsc5ur_logo%202.png",
  tradevesta: "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/56cp3jbh_image.png"
};

// Default bank details
const DEFAULT_BANK_DETAILS = {
  account_number: "",
  bank_name: "",
  branch_address: "",
  account_holder: "",
  swift_code: "",
  sort_code: "",
  other_details: ""
};

// Status badge component
function StatusBadge({ status }) {
  const statusStyles = {
    draft: "bg-zinc-500/20 text-zinc-400",
    sent: "bg-blue-500/20 text-blue-400",
    paid: "bg-green-500/20 text-green-400",
    cancelled: "bg-red-500/20 text-red-400"
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.draft}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

export default function InvoiceManagement() {
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [brands, setBrands] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filter states
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD for input, will convert to DD/MM/YYYY for API
    brand: 'brandstage',
    client: {
      company_name: '',
      address: '',
      gstin: '',
      contact_person: '',
      contact_designation: '',
      email: '',
      phone: ''
    },
    purpose: '',
    description: '',
    primary_currency: 'INR',  // Primary currency for amounts
    line_items: [{ description: '', details: '', quantity: 1, rate: 0 }],  // rate is in primary currency
    show_secondary_currency: false,  // Toggle for dual currency
    secondary_currency: '',  // USD, GBP, EUR, INR
    exchange_rate: null,
    exchange_rate_source: 'OANDA Currency Converter',
    include_gst: true,
    gst_rate: 18,
    bank_details: { ...DEFAULT_BANK_DETAILS },
    status: 'draft',
    notes: ''
  });
  
  // Template states
  const [bankTemplates, setBankTemplates] = useState([]);
  const [clientTemplates, setClientTemplates] = useState([]);
  const [savingBankTemplate, setSavingBankTemplate] = useState(false);
  const [savingClientTemplate, setSavingClientTemplate] = useState(false);
  const [newBankTemplateName, setNewBankTemplateName] = useState('');
  const [newClientTemplateName, setNewClientTemplateName] = useState('');
  const [showSaveBankModal, setShowSaveBankModal] = useState(false);
  const [showSaveClientModal, setShowSaveClientModal] = useState(false);
  
  // Summary state
  const [summary, setSummary] = useState({ total_amount_inr: 0, count: 0 });
  
  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterBrand) params.append('brand', filterBrand);
      if (filterStatus) params.append('status', filterStatus);
      if (filterMonth) params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);
      
      const response = await axios.get(`${API_URL}/api/admin/invoices?${params}`, { withCredentials: true });
      setInvoices(response.data.invoices || []);
      setSummary(response.data.summary || { total_amount_inr: 0, count: 0 });
    } catch (err) {
      setError('Failed to load invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterBrand, filterStatus, filterMonth, filterYear]);
  
  // Fetch brand configs
  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/invoices/brands`, { withCredentials: true });
      setBrands(response.data.brands || {});
    } catch (err) {
      console.error('Failed to load brands:', err);
    }
  }, []);
  
  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const [bankRes, clientRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/invoice-templates/bank`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/invoice-templates/client`, { withCredentials: true })
      ]);
      setBankTemplates(bankRes.data.templates || []);
      setClientTemplates(clientRes.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, []);
  
  useEffect(() => {
    fetchInvoices();
    fetchBrands();
    fetchTemplates();
  }, [fetchInvoices, fetchBrands, fetchTemplates]);
  
  // Save bank template
  const saveBankTemplate = async () => {
    if (!newBankTemplateName.trim()) {
      setError('Please enter a template name');
      return;
    }
    
    try {
      setSavingBankTemplate(true);
      await axios.post(`${API_URL}/api/admin/invoice-templates/bank`, {
        template_name: newBankTemplateName,
        bank_details: formData.bank_details
      }, { withCredentials: true });
      
      setSuccess('Bank template saved!');
      setShowSaveBankModal(false);
      setNewBankTemplateName('');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save bank template');
    } finally {
      setSavingBankTemplate(false);
    }
  };
  
  // Save client template
  const saveClientTemplate = async () => {
    if (!newClientTemplateName.trim()) {
      setError('Please enter a template name');
      return;
    }
    
    try {
      setSavingClientTemplate(true);
      await axios.post(`${API_URL}/api/admin/invoice-templates/client`, {
        template_name: newClientTemplateName,
        client_info: formData.client
      }, { withCredentials: true });
      
      setSuccess('Client template saved!');
      setShowSaveClientModal(false);
      setNewClientTemplateName('');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save client template');
    } finally {
      setSavingClientTemplate(false);
    }
  };
  
  // Load bank template
  const loadBankTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      bank_details: { ...template.bank_details }
    }));
  };
  
  // Load client template
  const loadClientTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      client: { ...template.client_info }
    }));
  };
  
  // Delete bank template
  const deleteBankTemplate = async (templateId) => {
    if (!window.confirm('Delete this bank template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoice-templates/bank/${templateId}`, { withCredentials: true });
      fetchTemplates();
    } catch (err) {
      setError('Failed to delete template');
    }
  };
  
  // Delete client template
  const deleteClientTemplate = async (templateId) => {
    if (!window.confirm('Delete this client template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoice-templates/client/${templateId}`, { withCredentials: true });
      fetchTemplates();
    } catch (err) {
      setError('Failed to delete template');
    }
  };
  
  // Generate invoice number
  const generateInvoiceNumber = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/invoices/generate-number/${formData.brand}`, { withCredentials: true });
      setFormData(prev => ({ ...prev, invoice_number: response.data.invoice_number }));
    } catch (err) {
      setError('Failed to generate invoice number');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0], // Reset to today
      brand: 'brandstage',
      client: {
        company_name: '',
        address: '',
        gstin: '',
        contact_person: '',
        contact_designation: '',
        email: '',
        phone: ''
      },
      purpose: '',
      description: '',
      primary_currency: 'INR',
      line_items: [{ description: '', details: '', quantity: 1, rate: 0 }],
      show_secondary_currency: false,
      secondary_currency: '',
      exchange_rate: null,
      exchange_rate_source: 'OANDA Currency Converter',
      include_gst: true,
      gst_rate: 18,
      bank_details: { ...DEFAULT_BANK_DETAILS },
      status: 'draft',
      notes: ''
    });
  };
  
  // Add line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', details: '', quantity: 1, rate: 0 }]
    }));
  };
  
  // Remove line item
  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
    }
  };
  
  // Update line item
  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, line_items: items };
    });
  };
  
  // Calculate totals
  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.quantity * (item.rate || 0)), 0);
    const gst = formData.include_gst ? subtotal * (formData.gst_rate / 100) : 0;
    const total = subtotal + gst;
    
    let secondaryTotal = null;
    if (formData.show_secondary_currency && formData.exchange_rate && formData.exchange_rate > 0) {
      secondaryTotal = total * formData.exchange_rate;
    }
    
    return { 
      subtotal, 
      gst, 
      total, 
      secondaryTotal,
      primaryCurrency: formData.primary_currency,
      secondaryCurrency: formData.secondary_currency
    };
  };
  
  // Get currency symbol
  const getCurrencySymbol = (currency) => {
    const symbols = { 'INR': '₹', 'USD': '$', 'GBP': '£', 'EUR': '€' };
    return symbols[currency] || currency;
  };
  
  // Create invoice
  const createInvoice = async () => {
    if (!formData.client.company_name || !formData.purpose || formData.line_items.length === 0) {
      setError('Please fill in required fields');
      return;
    }
    
    try {
      setActionLoading(true);
      setError('');
      
      // Convert date from YYYY-MM-DD to DD/MM/YYYY for API
      const apiData = {
        ...formData,
        invoice_date: formData.invoice_date ? 
          formData.invoice_date.split('-').reverse().join('/') : null
      };
      
      await axios.post(`${API_URL}/api/admin/invoices`, apiData, { withCredentials: true });
      
      setSuccess('Invoice created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchInvoices();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Update invoice
  const updateInvoice = async () => {
    if (!selectedInvoice) return;
    
    try {
      setActionLoading(true);
      setError('');
      
      // Convert date from YYYY-MM-DD to DD/MM/YYYY for API
      const apiData = {
        ...formData,
        invoice_date: formData.invoice_date ? 
          formData.invoice_date.split('-').reverse().join('/') : null
      };
      
      await axios.put(`${API_URL}/api/admin/invoices/${selectedInvoice.id}`, apiData, { withCredentials: true });
      
      setSuccess('Invoice updated successfully');
      setShowCreateModal(false);
      setSelectedInvoice(null);
      resetForm();
      fetchInvoices();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Delete invoice
  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      setActionLoading(true);
      await axios.delete(`${API_URL}/api/admin/invoices/${invoiceId}`, { withCredentials: true });
      setSuccess('Invoice deleted successfully');
      fetchInvoices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete invoice');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Open edit modal
  const openEditModal = (invoice) => {
    setSelectedInvoice(invoice);
    // Map old format to new format for backwards compatibility
    const lineItems = (invoice.line_items || []).map(item => ({
      description: item.description || '',
      details: item.details || '',
      quantity: item.quantity || 1,
      rate: item.rate || item.rate_inr || 0  // Support both old and new format
    }));
    
    // Convert invoice_date from DD/MM/YYYY to YYYY-MM-DD for the date input
    let invoiceDate = new Date().toISOString().split('T')[0];
    if (invoice.invoice_date) {
      const parts = invoice.invoice_date.split('/');
      if (parts.length === 3) {
        invoiceDate = `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD
      }
    }
    
    setFormData({
      invoice_number: invoice.invoice_number,
      invoice_date: invoiceDate,
      brand: invoice.brand,
      client: invoice.client || {},
      purpose: invoice.purpose,
      description: invoice.description || '',
      primary_currency: invoice.primary_currency || 'INR',
      line_items: lineItems.length > 0 ? lineItems : [{ description: '', details: '', quantity: 1, rate: 0 }],
      show_secondary_currency: !!invoice.secondary_currency || !!invoice.foreign_currency,
      secondary_currency: invoice.secondary_currency || invoice.foreign_currency || '',
      exchange_rate: invoice.exchange_rate || null,
      exchange_rate_source: invoice.exchange_rate_source || 'OANDA Currency Converter',
      include_gst: invoice.include_gst !== false,
      gst_rate: invoice.gst_rate || 18,
      bank_details: invoice.bank_details || { ...DEFAULT_BANK_DETAILS },
      status: invoice.status,
      notes: invoice.notes || ''
    });
    setShowCreateModal(true);
  };
  
  // View invoice
  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };
  
  const totals = calculateTotals();
  
  // Generate years for filter
  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) {
    years.push(y.toString());
  }
  
  // Generate months for filter
  const months = [
    { value: '', label: 'All Months' },
    { value: `${filterYear}-01`, label: 'January' },
    { value: `${filterYear}-02`, label: 'February' },
    { value: `${filterYear}-03`, label: 'March' },
    { value: `${filterYear}-04`, label: 'April' },
    { value: `${filterYear}-05`, label: 'May' },
    { value: `${filterYear}-06`, label: 'June' },
    { value: `${filterYear}-07`, label: 'July' },
    { value: `${filterYear}-08`, label: 'August' },
    { value: `${filterYear}-09`, label: 'September' },
    { value: `${filterYear}-10`, label: 'October' },
    { value: `${filterYear}-11`, label: 'November' },
    { value: `${filterYear}-12`, label: 'December' }
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Invoice Management</h2>
          <p className="text-zinc-400 text-sm">Create and manage invoices for BrandStage, Continex, and TradeVesta</p>
        </div>
        
        <button
          onClick={() => { resetForm(); setSelectedInvoice(null); setShowCreateModal(true); }}
          className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </button>
      </div>
      
      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-400">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <span className="text-green-400">{success}</span>
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All Brands</option>
            <option value="brandstage">BrandStage</option>
            <option value="continex">Continex</option>
            <option value="tradevesta">TradeVesta</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
          
          <select
            value={filterYear}
            onChange={(e) => { setFilterYear(e.target.value); setFilterMonth(''); }}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          
          <button
            onClick={fetchInvoices}
            className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          
          <button
            onClick={async () => {
              try {
                const params = new URLSearchParams();
                if (filterBrand) params.append('brand', filterBrand);
                if (filterMonth) params.append('month', filterMonth);
                if (filterYear) params.append('year', filterYear);
                
                const response = await axios.get(
                  `${API_URL}/api/admin/invoices/export/batch?${params}`,
                  { withCredentials: true }
                );
                
                if (response.data.invoices.length === 0) {
                  setError('No invoices to export for the selected filters');
                  return;
                }
                
                // Create a text summary with links
                const linksText = response.data.invoices.map(inv => 
                  `${inv.invoice_number} - ${inv.client_name} - ₹${inv.amount_inr?.toLocaleString()}\nView: ${inv.view_link}\nPDF: ${inv.pdf_link}\n`
                ).join('\n');
                
                await navigator.clipboard.writeText(linksText);
                setSuccess(`${response.data.invoices.length} invoice links copied! Send to your accountant.`);
                setTimeout(() => setSuccess(''), 5000);
              } catch (err) {
                setError('Failed to generate export links');
              }
            }}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
          >
            <Send className="w-4 h-4" />
            Export for Accountant
          </button>
        </div>
        
        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-zinc-700 flex gap-6 text-sm">
          <span className="text-zinc-400">Total Invoices: <span className="text-white font-bold">{summary.count}</span></span>
          <span className="text-zinc-400">Total Amount: <span className="text-white font-bold">₹{summary.total_amount_inr?.toLocaleString()}</span></span>
        </div>
      </div>
      
      {/* Invoice List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No invoices found</p>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="mt-4 text-[#C41E3A] hover:underline"
          >
            Create your first invoice
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map(invoice => (
            <div key={invoice.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <img 
                    src={BRAND_LOGOS[invoice.brand]} 
                    alt={invoice.brand}
                    className="w-12 h-12 object-contain bg-white rounded-lg p-1"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-white">{invoice.invoice_number}</h3>
                      <StatusBadge status={invoice.status} />
                      {invoice.auto_generated && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${invoice.client_email_status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`} data-testid={`invoice-auto-badge-${invoice.id}`}>
                          {invoice.client_email_status === 'sent' ? 'Online · emailed' : 'Online · email pending'}
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-400">{invoice.client?.company_name}</p>
                    <p className="text-zinc-500 text-sm">{invoice.purpose}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-xl font-bold text-white">
                    {invoice.foreign_currency && invoice.totals?.grand_total_foreign ? (
                      <>
                        {invoice.foreign_currency} {invoice.totals.grand_total_foreign.toLocaleString()}
                        <span className="text-zinc-500 text-sm block">₹{invoice.totals.grand_total_inr?.toLocaleString()}</span>
                      </>
                    ) : (
                      <>₹{invoice.totals?.grand_total_inr?.toLocaleString()}</>
                    )}
                  </p>
                  <p className="text-zinc-500 text-sm">{invoice.invoice_date}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-700">
                <button
                  onClick={() => viewInvoice(invoice)}
                  className="flex items-center gap-1 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.get(
                        `${API_URL}/api/admin/invoices/${invoice.id}/pdf`,
                        { withCredentials: true, responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      setError('Failed to download PDF');
                    }
                  }}
                  className="flex items-center gap-1 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.get(
                        `${API_URL}/api/admin/invoices/${invoice.id}/share-link`,
                        { withCredentials: true }
                      );
                      await navigator.clipboard.writeText(response.data.share_link);
                      setSuccess(`Link copied! Share with ${invoice.client?.company_name}`);
                      setTimeout(() => setSuccess(''), 3000);
                    } catch (err) {
                      setError('Failed to generate share link');
                    }
                  }}
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
                <button
                  onClick={() => setEmailInvoice(invoice)}
                  className="flex items-center gap-1 bg-[#C41E3A] hover:bg-[#A01830] text-white px-3 py-2 rounded-lg text-sm transition-colors"
                  data-testid={`invoice-email-btn-${invoice.id}`}
                >
                  <Send className="w-4 h-4" />
                  Email
                </button>
                <button
                  onClick={() => openEditModal(invoice)}
                  className="flex items-center gap-1 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => deleteInvoice(invoice.id)}
                  disabled={actionLoading}
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {emailInvoice && (
        <InvoiceEmailModal
          invoice={emailInvoice}
          onClose={() => setEmailInvoice(null)}
          onSent={() => { setEmailInvoice(null); fetchInvoices(); }}
        />
      )}

      {/* Create/Edit Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-4xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {selectedInvoice ? 'Edit Invoice' : 'Create New Invoice'}
              </h2>
              <button
                onClick={() => { setShowCreateModal(false); setSelectedInvoice(null); resetForm(); }}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
              {/* Brand Selection - Visual Cards */}
              <div>
                <label className="block text-zinc-400 text-sm mb-3">Select Brand *</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'brandstage', name: 'BrandStage', note: 'Business arm of Continex' },
                    { id: 'continex', name: 'Continex', note: 'Trading Authority' },
                    { id: 'tradevesta', name: 'TradeVesta', note: 'Business arm of Continex' }
                  ].map(brand => (
                    <button
                      key={brand.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, brand: brand.id }))}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        formData.brand === brand.id 
                          ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                          : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <img 
                        src={BRAND_LOGOS[brand.id]} 
                        alt={brand.name}
                        className="h-8 object-contain mb-2"
                      />
                      <p className="text-white font-medium text-sm">{brand.name}</p>
                      <p className="text-zinc-500 text-xs">{brand.note}</p>
                    </button>
                  ))}
                </div>
                <p className="text-zinc-500 text-xs mt-2">
                  All invoices are issued by Continex Tradeline India Pvt Ltd. Brand logo and attribution will appear on the invoice.
                </p>
              </div>
              
              {/* Invoice Number */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Invoice Number</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                    placeholder="Manual entry or generate"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  />
                  <button
                    type="button"
                    onClick={generateInvoiceNumber}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Generate
                  </button>
                </div>
              </div>
              
              {/* Invoice Date */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Invoice Date</label>
                <input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                />
              </div>
              
              {/* Client Info */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Client Information</h3>
                  <div className="flex items-center gap-2">
                    {clientTemplates.length > 0 && (
                      <div className="relative group">
                        <select
                          data-testid="client-template-select"
                          onChange={(e) => {
                            const template = clientTemplates.find(t => t.id === e.target.value);
                            if (template) {
                              loadClientTemplate(template);
                              setSuccess(`Loaded client template: ${template.template_name}`);
                            }
                          }}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm pr-8"
                          defaultValue=""
                        >
                          <option value="" disabled>Load saved client...</option>
                          {clientTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.template_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      type="button"
                      data-testid="save-client-template-btn"
                      onClick={() => setShowSaveClientModal(true)}
                      disabled={!formData.client.company_name}
                      className="text-[#C41E3A] text-sm hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      Save as template
                    </button>
                  </div>
                </div>
                {/* Saved client templates with delete */}
                {clientTemplates.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {clientTemplates.map(t => (
                      <div
                        key={t.id}
                        className="bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-sm flex items-center gap-2 group"
                      >
                        <span 
                          className="text-zinc-300 cursor-pointer hover:text-white"
                          onClick={() => {
                            loadClientTemplate(t);
                            setSuccess(`Loaded: ${t.template_name}`);
                          }}
                        >
                          {t.template_name}
                        </span>
                        <button
                          type="button"
                          data-testid={`delete-client-template-${t.id}`}
                          onClick={() => deleteClientTemplate(t.id)}
                          className="text-zinc-500 hover:text-red-500 transition-colors"
                          title="Delete template"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Company Name *</label>
                    <input
                      type="text"
                      value={formData.client.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, company_name: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">GSTIN</label>
                    <input
                      type="text"
                      value={formData.client.gstin}
                      onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, gstin: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-zinc-400 text-sm mb-1">Address</label>
                    <textarea
                      value={formData.client.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, address: e.target.value } }))}
                      rows={2}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Contact Person (K/A)</label>
                    <input
                      type="text"
                      value={formData.client.contact_person}
                      onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, contact_person: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Designation</label>
                    <input
                      type="text"
                      value={formData.client.contact_designation}
                      onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, contact_designation: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Purpose & Description */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Purpose *</label>
                  <input
                    type="text"
                    value={formData.purpose}
                    onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                    placeholder="e.g., Reimbursement of Travel expenses"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              
              {/* Line Items */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Line Items</h3>
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="text-[#C41E3A] text-sm hover:underline"
                  >
                    + Add Item
                  </button>
                </div>
                
                <div className="space-y-3">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Description"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div className="w-20">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          placeholder="Qty"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          value={item.rate || ''}
                          onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          placeholder={`Rate (${getCurrencySymbol(formData.primary_currency)})`}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        disabled={formData.line_items.length === 1}
                        className="text-red-400 hover:text-red-300 p-2 disabled:opacity-30"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Currency Selection */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-4">Currency Settings</h3>
                
                {/* Primary Currency */}
                <div className="mb-4">
                  <label className="block text-zinc-400 text-sm mb-2">Primary Currency (amounts entered in this currency) *</label>
                  <div className="flex gap-2">
                    {['INR', 'USD', 'GBP', 'EUR'].map(currency => (
                      <button
                        key={currency}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, primary_currency: currency }))}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          formData.primary_currency === currency
                            ? 'bg-[#C41E3A] text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {getCurrencySymbol(currency)} {currency}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Secondary Currency Toggle */}
                <div className="pt-4 border-t border-zinc-700">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_secondary_currency}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        show_secondary_currency: e.target.checked,
                        secondary_currency: e.target.checked ? (prev.primary_currency === 'INR' ? 'USD' : 'INR') : ''
                      }))}
                      className="w-4 h-4 rounded bg-zinc-800 border-zinc-700"
                    />
                    <div>
                      <span className="text-white font-medium">Show Secondary Currency</span>
                      <p className="text-zinc-500 text-xs">Enable for reimbursements or when you need to show converted amounts</p>
                    </div>
                  </label>
                  
                  {formData.show_secondary_currency && (
                    <div className="mt-4 grid md:grid-cols-3 gap-4 bg-zinc-800/50 p-4 rounded-lg">
                      <div>
                        <label className="block text-zinc-400 text-sm mb-1">Secondary Currency</label>
                        <select
                          value={formData.secondary_currency}
                          onChange={(e) => setFormData(prev => ({ ...prev, secondary_currency: e.target.value }))}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                        >
                          <option value="">Select currency</option>
                          {['INR', 'USD', 'GBP', 'EUR'].filter(c => c !== formData.primary_currency).map(currency => (
                            <option key={currency} value={currency}>{currency}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-zinc-400 text-sm mb-1">
                          Exchange Rate (1 {formData.primary_currency} = ? {formData.secondary_currency || '...'})
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          value={formData.exchange_rate || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, exchange_rate: parseFloat(e.target.value) || null }))}
                          placeholder="e.g., 0.012 or 83.5"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-zinc-400 text-sm mb-1">Rate Source</label>
                        <input
                          type="text"
                          value={formData.exchange_rate_source}
                          onChange={(e) => setFormData(prev => ({ ...prev, exchange_rate_source: e.target.value }))}
                          placeholder="e.g., OANDA"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* GST */}
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Tax / GST</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_gst}
                      onChange={(e) => setFormData(prev => ({ ...prev, include_gst: e.target.checked }))}
                      className="w-4 h-4 rounded bg-zinc-800 border-zinc-700"
                    />
                    <span className="text-white">Include GST</span>
                  </label>
                  {formData.include_gst && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={formData.gst_rate}
                        onChange={(e) => setFormData(prev => ({ ...prev, gst_rate: parseFloat(e.target.value) || 18 }))}
                        className="w-20 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      />
                      <span className="text-zinc-400">%</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Bank Details */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Bank Details</h3>
                  <div className="flex items-center gap-2">
                    {bankTemplates.length > 0 && (
                      <div className="relative group">
                        <select
                          data-testid="bank-template-select"
                          onChange={(e) => {
                            const template = bankTemplates.find(t => t.id === e.target.value);
                            if (template) {
                              loadBankTemplate(template);
                              setSuccess(`Loaded bank template: ${template.template_name}`);
                            }
                          }}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm pr-8"
                          defaultValue=""
                        >
                          <option value="" disabled>Load saved bank...</option>
                          {bankTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.template_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      type="button"
                      data-testid="save-bank-template-btn"
                      onClick={() => setShowSaveBankModal(true)}
                      disabled={!formData.bank_details.account_number}
                      className="text-[#C41E3A] text-sm hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      Save as template
                    </button>
                  </div>
                </div>
                {/* Saved bank templates with delete */}
                {bankTemplates.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {bankTemplates.map(t => (
                      <div
                        key={t.id}
                        className="bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-sm flex items-center gap-2 group"
                      >
                        <span 
                          className="text-zinc-300 cursor-pointer hover:text-white"
                          onClick={() => {
                            loadBankTemplate(t);
                            setSuccess(`Loaded: ${t.template_name}`);
                          }}
                        >
                          {t.template_name}
                        </span>
                        <button
                          type="button"
                          data-testid={`delete-bank-template-${t.id}`}
                          onClick={() => deleteBankTemplate(t.id)}
                          className="text-zinc-500 hover:text-red-500 transition-colors"
                          title="Delete template"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Account Holder</label>
                    <input
                      type="text"
                      value={formData.bank_details.account_holder}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, account_holder: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_details.account_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, account_number: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_details.bank_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, bank_name: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Branch Address</label>
                    <input
                      type="text"
                      value={formData.bank_details.branch_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, branch_address: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">SWIFT Code</label>
                    <input
                      type="text"
                      value={formData.bank_details.swift_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, swift_code: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Sort Code</label>
                    <input
                      type="text"
                      value={formData.bank_details.sort_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, sort_code: e.target.value } }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-zinc-400 text-sm mb-1">Other Details</label>
                    <input
                      type="text"
                      value={formData.bank_details.other_details}
                      onChange={(e) => setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, other_details: e.target.value } }))}
                      placeholder="Any other payment information..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Totals Preview */}
              <div className="bg-zinc-800 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3">Totals Preview</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Subtotal:</span>
                    <span className="text-white">{getCurrencySymbol(formData.primary_currency)}{totals.subtotal.toLocaleString()}</span>
                  </div>
                  {formData.include_gst && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">GST ({formData.gst_rate}%):</span>
                      <span className="text-white">{getCurrencySymbol(formData.primary_currency)}{totals.gst.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t border-zinc-700 pt-2">
                    <span className="text-white">Grand Total ({formData.primary_currency}):</span>
                    <span className="text-white">{getCurrencySymbol(formData.primary_currency)}{totals.total.toLocaleString()}</span>
                  </div>
                  {formData.show_secondary_currency && totals.secondaryTotal && formData.secondary_currency && (
                    <div className="flex justify-between text-[#C41E3A]">
                      <span>Converted ({formData.secondary_currency}):</span>
                      <span>{getCurrencySymbol(formData.secondary_currency)}{totals.secondaryTotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-zinc-700">
              <button
                onClick={selectedInvoice ? updateInvoice : createInvoice}
                disabled={actionLoading}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    {selectedInvoice ? 'Update Invoice' : 'Create Invoice'}
                  </>
                )}
              </button>
              <button
                onClick={() => { setShowCreateModal(false); setSelectedInvoice(null); resetForm(); }}
                className="px-6 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white text-black rounded-lg p-8 max-w-3xl w-full my-8">
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8">
              <img 
                src={BRAND_LOGOS[selectedInvoice.brand]} 
                alt={selectedInvoice.brand}
                className="h-16 object-contain"
              />
              <div className="text-right text-sm">
                <p className="font-bold">{selectedInvoice.brand_config?.name}</p>
                <p className="text-gray-600">{selectedInvoice.brand_config?.address}</p>
                <p className="text-gray-600">TIN: {selectedInvoice.brand_config?.tin}</p>
                <p className="text-gray-600">GSTIN: {selectedInvoice.brand_config?.gst}</p>
              </div>
            </div>
            
            {/* Invoice Info */}
            <div className="flex justify-between mb-6">
              <div>
                <p className="text-gray-600">Bill No:</p>
                <p className="font-bold text-lg">{selectedInvoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-600">Bill Date:</p>
                <p className="font-bold">{selectedInvoice.invoice_date}</p>
              </div>
            </div>
            
            {/* Client Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-600 text-sm">Issued to:</p>
              <p className="font-bold">{selectedInvoice.client?.company_name}</p>
              <p>{selectedInvoice.client?.address}</p>
              {selectedInvoice.client?.contact_person && (
                <p>K/A: {selectedInvoice.client.contact_person}</p>
              )}
            </div>
            
            {/* Purpose */}
            <div className="mb-6">
              <p className="text-gray-600 text-sm">Description of services:</p>
              <p className="font-medium">{selectedInvoice.purpose}</p>
            </div>
            
            {/* Line Items Table */}
            <table className="w-full mb-6">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-2">Description of Charges</th>
                  <th className="text-right py-2">INR</th>
                  {selectedInvoice.foreign_currency && (
                    <th className="text-right py-2">{selectedInvoice.foreign_currency}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {selectedInvoice.line_items?.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-200">
                    <td className="py-2">{item.description}</td>
                    <td className="text-right py-2">₹{(item.quantity * item.rate_inr).toLocaleString()}</td>
                    {selectedInvoice.foreign_currency && selectedInvoice.exchange_rate && (
                      <td className="text-right py-2">
                        {((item.quantity * item.rate_inr) / selectedInvoice.exchange_rate).toFixed(2)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold border-t-2 border-gray-300">
                  <td className="py-2">Grand Total</td>
                  <td className="text-right py-2">₹{selectedInvoice.totals?.grand_total_inr?.toLocaleString()}</td>
                  {selectedInvoice.foreign_currency && (
                    <td className="text-right py-2">
                      {selectedInvoice.foreign_currency} {selectedInvoice.totals?.grand_total_foreign?.toLocaleString()}
                    </td>
                  )}
                </tr>
              </tfoot>
            </table>
            
            {/* Amount in Words */}
            <div className="mb-6 p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Amount in words:</p>
              <p className="font-medium">{selectedInvoice.amount_in_words}</p>
            </div>
            
            {/* Bank Details */}
            <div className="mb-6">
              <p className="font-bold mb-2">Bank Details:</p>
              <div className="text-sm space-y-1">
                <p>Account No: {selectedInvoice.bank_details?.account_number}</p>
                <p>Bank: {selectedInvoice.bank_details?.bank_name}</p>
                <p>Branch: {selectedInvoice.bank_details?.branch_address}</p>
                <p>Account Holder: {selectedInvoice.bank_details?.account_holder}</p>
                {selectedInvoice.bank_details?.swift_code && (
                  <p>SWIFT: {selectedInvoice.bank_details.swift_code}</p>
                )}
              </div>
            </div>
            
            {/* Exchange Rate Note */}
            {selectedInvoice.exchange_rate && (
              <p className="text-sm text-gray-500 mb-4">
                * Conversion rate as per {selectedInvoice.exchange_rate_source || 'OANDA'}: 
                1 {selectedInvoice.foreign_currency} = ₹{selectedInvoice.exchange_rate}
              </p>
            )}
            
            {/* Footer */}
            <div className="flex justify-between items-end text-sm text-gray-600 border-t pt-4">
              <p>E. & O. E.</p>
              <div className="text-right">
                <p className="mb-8">Authorised Signatory</p>
                <p>(Signature affixed)</p>
              </div>
            </div>
            
            {/* Close Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => { setShowViewModal(false); setSelectedInvoice(null); }}
                className="bg-zinc-900 hover:bg-zinc-800 text-white px-6 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Bank Template Modal */}
      {showSaveBankModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full" data-testid="save-bank-template-modal">
            <h3 className="text-lg font-bold text-white mb-4">Save Bank Details Template</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Save these bank details for quick reuse in future invoices.
            </p>
            <input
              type="text"
              data-testid="bank-template-name-input"
              value={newBankTemplateName}
              onChange={(e) => setNewBankTemplateName(e.target.value)}
              placeholder="Template name (e.g., HDFC Chennai, USD Account)"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white mb-4"
            />
            <div className="flex gap-3">
              <button
                data-testid="confirm-save-bank-template-btn"
                onClick={saveBankTemplate}
                disabled={savingBankTemplate}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2 rounded-lg font-medium disabled:opacity-50"
              >
                {savingBankTemplate ? 'Saving...' : 'Save Template'}
              </button>
              <button
                onClick={() => { setShowSaveBankModal(false); setNewBankTemplateName(''); }}
                className="px-4 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Client Template Modal */}
      {showSaveClientModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full" data-testid="save-client-template-modal">
            <h3 className="text-lg font-bold text-white mb-4">Save Client Template</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Save this client info for quick reuse in future invoices.
            </p>
            <input
              type="text"
              data-testid="client-template-name-input"
              value={newClientTemplateName}
              onChange={(e) => setNewClientTemplateName(e.target.value)}
              placeholder="Template name (e.g., PharmaCorp Ghana, SHPG Solutions)"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white mb-4"
            />
            <div className="flex gap-3">
              <button
                data-testid="confirm-save-client-template-btn"
                onClick={saveClientTemplate}
                disabled={savingClientTemplate}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2 rounded-lg font-medium disabled:opacity-50"
              >
                {savingClientTemplate ? 'Saving...' : 'Save Template'}
              </button>
              <button
                onClick={() => { setShowSaveClientModal(false); setNewClientTemplateName(''); }}
                className="px-4 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
